"use client";

import { useState } from "react";
import axiosInstance from "@libs/axios";
import { notifications } from "@mantine/notifications";

interface CopyDocPabeanData {
  BatchID?: number;
  BatchUUID?: string;
  JarvisProcessID?: number;
  batchId?: number;
  batchUUID?: string;
  batch_uuid?: string;
  jarvisProcessId?: number;
}

interface CopyDocPabeanResponse {
  success?: boolean;
  data?: CopyDocPabeanData | null;
  message?: string;
}

function extractCopiedBatchUUID(payload: CopyDocPabeanResponse | null | undefined): string | null {
  const data = payload?.data;
  if (!data || typeof data !== "object") return null;
  const raw = data.BatchUUID ?? data.batchUUID ?? data.batch_uuid;
  if (raw == null) return null;
  const uuid = String(raw).trim();
  return uuid || null;
}

export default function useCopyDocPabean() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copyDocPabean = async (uuid: string, shipmentType: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await axiosInstance.post<CopyDocPabeanResponse>(
        `/api/v1/ceisa/copies/uuid/${uuid}?shipmentType=${shipmentType}`
      );

      const batchUUID = extractCopiedBatchUUID(data);
      // Treat HTTP 200 + a new batch UUID as success even when `success` is
      // omitted/renamed — navigation must not depend on a brittle flag alone.
      const ok = data?.success !== false && Boolean(batchUUID);

      if (ok && batchUUID) {
        notifications.show({
          title: "Success",
          message: data?.message || "Document copied successfully",
          color: "green",
        });
        return batchUUID;
      }

      throw new Error(data?.message || "Failed to copy document");
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to copy document";
      setError(errorMessage);
      notifications.show({ title: "Error", message: errorMessage, color: "red" });
      return null;
    } finally {
      // Clear after request — list tab stays open when copy opens a new tab.
      setLoading(false);
    }
  };

  return {
    copyDocPabean,
    loading,
    error,
  };
} 